import React, { useEffect, useState, useMemo, useRef } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { customIndicators, fetchStocks, fetchIndices } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Area, ComposedChart } from "recharts";
import { toast } from "sonner";
import { Search, Activity, Zap, TrendingUp, AlertTriangle, Target, Clock, ArrowUpRight, ArrowDownRight, Maximize, Command, Crosshair } from "lucide-react"; 

export default function IndicatorsPage() {
  const [symbol, setSymbol] = useState("");
  const [symbols, setSymbols] = useState([]);
  
  // --- Autocomplete States ---
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapperRef = useRef(null);

  // --- Parameters State ---
  const [timeframe, setTimeframe] = useState("daily"); 
  const [ema, setEma] = useState(10);
  const [sma, setSma] = useState(10);
  const [rsiP, setRsiP] = useState(14);
  const [tailLength, setTailLength] = useState(150); 
  
  const [data, setData] = useState([]);
  const [signals, setSignals] = useState({});
  const [currentPrice, setCurrentPrice] = useState(null);
  const [prevPrice, setPrevPrice] = useState(null);
  const [busy, setBusy] = useState(false);

  // Fetch initial symbols
  useEffect(() => {
    (async () => {
      try {
        const [s, i] = await Promise.all([fetchStocks(), fetchIndices()]);
        const all = [...(i.data.indices || []), ...(s.data.stocks || [])];
        setSymbols(all);
        if (!symbol && all.length) {
          setSymbol("Nifty 50"); 
          setSearchQuery("Nifty 50");
        }
      } catch (e) {
        toast.error("Failed to load symbol list");
      }
    })();
  }, []); 

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Global Keyboard Shortcut (Ctrl+K) for Search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('symbol-search')?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredSymbols = useMemo(() => {
    if (!searchQuery) return symbols.slice(0, 50);
    const lowerQ = searchQuery.toLowerCase();
    return symbols.filter(s => s.toLowerCase().includes(lowerQ)).slice(0, 50);
  }, [searchQuery, symbols]);

  const safeParseInt = (val, min = 2) => {
    const parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed < min) return min;
    return parsed;
  };

  const run = async () => {
    if (!symbol) return;
    
    const safeEma = safeParseInt(ema, 2);
    const safeSma = safeParseInt(sma, 2);
    const safeRsi = safeParseInt(rsiP, 2);
    
    if (safeEma !== parseInt(ema)) setEma(safeEma);
    if (safeSma !== parseInt(sma)) setSma(safeSma);
    if (safeRsi !== parseInt(rsiP)) setRsiP(safeRsi);

    setBusy(true);
    try {
      const { data: resp } = await customIndicators({
        symbol, timeframe, ema_period: safeEma, sma_period: safeSma, rsi_period: safeRsi,
      });
      const cleanData = (resp.data || []).filter(d => d.ema !== null && d.sma !== null && d.close !== null);
      setData(cleanData);
      if (resp.signals) setSignals(resp.signals);
      
      if (cleanData.length >= 2) {
        setCurrentPrice(cleanData[cleanData.length - 1].close);
        setPrevPrice(cleanData[cleanData.length - 2].close);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch indicators");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { 
    if (!symbol) return;
    const timer = setTimeout(() => run(), 500); 
    return () => clearTimeout(timer);
  }, [symbol, timeframe, ema, sma, rsiP]); 

  // 🔥 BUG FIX: Smart Data Scaling based on requested tailLength 🔥
  const tail = useMemo(() => {
     if(!data.length) return [];
     // Ensure we don't try to slice more than we have
     const actualLength = Math.min(tailLength, data.length);
     return data.slice(-actualLength);
  }, [data, tailLength]);
  
  const emaK = "ema", smaK = "sma", rsiK = "rsi";

  const absChange = currentPrice && prevPrice ? (currentPrice - prevPrice) : 0;
  const pctChange = prevPrice ? ((absChange / prevPrice) * 100) : 0;
  const isPositive = absChange >= 0;

  const viewStats = useMemo(() => {
    if (!tail.length) return { high: 0, low: 0, avgRsi: 0 };
    const highs = tail.map(d => d.high).filter(Boolean);
    const lows = tail.map(d => d.low).filter(Boolean);
    const rsis = tail.map(d => d.rsi).filter(Boolean);
    
    return {
      high: highs.length ? Math.max(...highs).toFixed(2) : "-",
      low: lows.length ? Math.min(...lows).toFixed(2) : "-",
      avgRsi: rsis.length ? (rsis.reduce((a, b) => a + b, 0) / rsis.length).toFixed(1) : "-"
    };
  }, [tail]);

  // 🔥 INSTITUTIONAL DARK TOOLTIP & RSI FIX 🔥
  const CustomPriceTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-slate-900/95 text-white p-3 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-xs border border-slate-700 min-w-[200px] backdrop-blur-md z-50">
          <div className="font-black text-sky-400 mb-2 border-b border-slate-700 pb-1.5 flex justify-between">
            <span>{d.date}</span>
            <span className="text-slate-400 font-medium">OHLC</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
            <div className="flex justify-between"><span className="text-slate-400">Open:</span> <span className="font-mono">{d.open?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">High:</span> <span className="font-mono text-emerald-400">{d.high?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Low:</span> <span className="font-mono text-rose-400">{d.low?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Close:</span> <span className="font-mono font-bold text-white">{d.close?.toFixed(2)}</span></div>
          </div>
          <div className="pt-2 border-t border-slate-700/50 space-y-1">
            <div className="flex justify-between"><span className="text-sky-400 font-semibold">EMA:</span> <span className="font-mono">{d.ema?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-amber-400 font-semibold">SMA:</span> <span className="font-mono">{d.sma?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-emerald-400 font-semibold">Supertrend:</span> <span className="font-mono">{d.supertrend?.toFixed(2)}</span></div>
            {/* Display formatted RSI directly in tooltip for combined insight */}
            <div className="flex justify-between border-t border-slate-700/30 pt-1 mt-1"><span className="text-purple-400 font-semibold">RSI:</span> <span className="font-mono">{d.rsi?.toFixed(2)}</span></div>
          </div>
        </div>
      );
    }
    return null;
  };

  // 🔥 NEW FEATURE: Insight Generator Logic 🔥
  const generateInsight = () => {
    if (!signals.trend) return "Awaiting signal compilation...";
    
    let insight = "";
    if (signals.trend === 'BULLISH' && signals.momentum !== 'OVERBOUGHT') {
        insight = "Asset is in a confirmed Bullish Trend with room for momentum. Supertrend confirms favorable buying conditions. Hold or look for accumulation on dips.";
    } else if (signals.trend === 'BULLISH' && signals.momentum === 'OVERBOUGHT') {
        insight = "Strong Bullish momentum, but RSI indicates Overbought conditions. Risk of near-term pullback is high. Tighten trailing stop-losses.";
    } else if (signals.trend === 'BEARISH' && signals.momentum === 'OVERSOLD') {
        insight = "Asset is in a Bearish trend but deeply Oversold. Watch for potential dead-cat bounces or reversal crossovers in EMA before taking new positions.";
    } else if (signals.trend === 'BEARISH') {
        insight = "Clear Bearish structure detected. Moving averages indicate sustained selling pressure. Supertrend suggests avoiding long positions.";
    }

    if (signals.breakout && signals.breakout !== 'NONE') {
        insight += `🚨 KEY ALERT: Fresh ${signals.breakout} detected in the most recent session!`;
    }

    return insight;
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col bg-slate-100 overflow-hidden relative">
      
      {/* Decorative Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-0"></div>

      {/* 🚀 HEADER CONTROLS 🚀 */}
      <div className="bg-white/90 backdrop-blur-md border-b px-4 py-2 flex items-center justify-between shadow-sm z-50 shrink-0 relative">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-sky-600" />
          <h1 className="text-lg font-black tracking-tight text-slate-900 font-mono">
            AI TERMINAL
          </h1>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="w-[280px] relative z-[100]" ref={wrapperRef}>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 absolute left-3 text-slate-400" />
              <Input 
                id="symbol-search"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setDropdownOpen(true); }}
                onFocus={() => setDropdownOpen(true)}
                placeholder="Search Asset..."
                className="pl-9 pr-12 h-9 text-xs bg-slate-50 focus:bg-white font-bold border-slate-300 transition-all shadow-sm"
              />
              <div className="absolute right-2 flex items-center gap-0.5 text-[10px] font-bold text-slate-400 border border-slate-200 bg-white px-1.5 py-0.5 rounded pointer-events-none">
                <Command className="w-3 h-3" /> K
              </div>
            </div>
            {dropdownOpen && (
              <div className="absolute top-[40px] left-0 w-full bg-white border border-slate-200 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.3)] z-[9999] max-h-64 overflow-y-auto flex flex-col">
                {filteredSymbols.length > 0 ? (
                  filteredSymbols.map((s) => (
                    <button
                      key={s} type="button"
                      className="text-left px-4 py-2.5 text-xs hover:bg-sky-50 focus:bg-sky-50 outline-none border-b border-slate-100 last:border-0 font-bold text-slate-700"
                      onClick={() => { setSymbol(s); setSearchQuery(s); setDropdownOpen(false); }}
                    >
                      {s}
                    </button>
                  ))
                ) : <span className="p-3 text-xs text-slate-500 text-center">No assets found.</span>}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-l pl-4 border-slate-200">
            <Label className="text-[10px] font-bold text-slate-500">EMA</Label>
            <Input type="number" min="2" value={ema} onChange={(e) => setEma(e.target.value)} className="w-14 h-8 text-xs font-bold text-center border-slate-200 shadow-sm" />
            <Label className="text-[10px] font-bold text-slate-500">SMA</Label>
            <Input type="number" min="2" value={sma} onChange={(e) => setSma(e.target.value)} className="w-14 h-8 text-xs font-bold text-center border-slate-200 shadow-sm" />
            <Label className="text-[10px] font-bold text-slate-500">RSI</Label>
            <Input type="number" min="2" value={rsiP} onChange={(e) => setRsiP(e.target.value)} className="w-14 h-8 text-xs font-bold text-center border-slate-200 shadow-sm" />
            
            <div className="ml-2 w-[100px] flex justify-center">
              {busy ? (
                <span className="flex items-center gap-1.5 text-[10px] font-bold text-sky-600 bg-sky-50 px-2 py-1.5 rounded-md border border-sky-100 shadow-inner">
                  <span className="animate-spin w-1.5 h-1.5 bg-sky-600 rounded-full"></span> Syncing
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1.5 rounded-md border border-emerald-100 shadow-sm">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full shadow-[0_0_4px_#10b981]"></span> Live Mode
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 MAIN DASHBOARD GRID 🚀 */}
      <div className="flex-1 flex min-h-0 p-3 gap-3 relative z-10">
        
        {/* LEFT PANEL: SIGNALS & LIVE PRICE */}
        <aside className="w-[280px] flex flex-col gap-3 shrink-0">
          
          <div className="flex bg-white/90 backdrop-blur p-1 rounded-lg border border-slate-200 shadow-sm shrink-0">
            {['daily', 'weekly', 'monthly'].map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`flex-1 py-1.5 text-xs font-bold capitalize rounded-md transition-all ${timeframe === tf ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
              >
                {tf === 'daily' ? '1D' : tf === 'weekly' ? '1W' : '1M'}
              </button>
            ))}
          </div>

          {currentPrice && (
              <Card className="bg-slate-900 text-white p-4 rounded-xl shadow-lg border border-slate-800 shrink-0 relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mr-4 -mt-4 opacity-10"><TrendingUp className="w-24 h-24" /></div>
                  <div className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mb-1 truncate w-4/5">{symbol}</div>
                  <div className="text-3xl font-black font-mono tracking-tight flex items-baseline gap-2">
                    ₹{currentPrice.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                  </div>
                  <div className={`mt-1 flex items-center gap-1 font-bold text-sm ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    <span>{absChange > 0 ? '+' : ''}{absChange.toFixed(2)}</span>
                    <span className="opacity-75">({pctChange > 0 ? '+' : ''}{pctChange.toFixed(2)}%)</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-slate-700/50 pt-3">
                      <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${signals.trend === 'BULLISH' ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${signals.trend === 'BULLISH' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        </span>
                        <span className="text-[10px] font-bold text-slate-300 tracking-wider">LIVE ALGO</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono flex items-center gap-1"><Clock className="w-3 h-3"/> {timeframe.toUpperCase()}</span>
                  </div>
              </Card>
          )}

          {Object.keys(signals).length > 0 && (
            <div className="flex-1 overflow-y-auto pr-1 space-y-3 custom-scrollbar">
                <Card className={`p-3 border-l-4 shadow-sm backdrop-blur-sm ${signals.trend === 'BULLISH' ? 'border-l-emerald-500 bg-emerald-50/80' : 'border-l-red-500 bg-red-50/80'}`}>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Trend Direction</div>
                    <div className={`text-base font-black ${signals.trend === 'BULLISH' ? 'text-emerald-700' : 'text-red-700'}`}>{signals.trend}</div>
                </Card>
                
                <Card className={`p-3 border-l-4 shadow-sm backdrop-blur-sm ${signals.momentum === 'OVERBOUGHT' ? 'border-l-orange-500 bg-orange-50/80' : (signals.momentum === 'OVERSOLD' ? 'border-l-emerald-500 bg-emerald-50/80' : 'border-l-slate-400 bg-slate-50/80')}`}>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> RSI Momentum</div>
                    <div className={`text-base font-black ${signals.momentum === 'OVERBOUGHT' ? 'text-orange-700' : (signals.momentum === 'OVERSOLD' ? 'text-emerald-700' : 'text-slate-700')}`}>{signals.momentum}</div>
                </Card>
                
                <Card className={`p-3 border-l-4 shadow-sm backdrop-blur-sm ${signals.supertrend === 'BUY' ? 'border-l-sky-500 bg-sky-50/80' : 'border-l-rose-500 bg-rose-50/80'}`}>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Supertrend Algo</div>
                    <div className={`text-base font-black ${signals.supertrend === 'BUY' ? 'text-sky-700' : 'text-rose-700'}`}>{signals.supertrend}</div>
                </Card>

                <Card className={`p-3 border-l-4 shadow-sm backdrop-blur-sm ${signals.breakout === 'BULLISH CROSSOVER' ? 'border-l-emerald-500 bg-emerald-50/80' : (signals.breakout === 'BEARISH CROSSDOWN' ? 'border-l-red-500 bg-red-50/80' : 'border-l-slate-200 bg-white/80')}`}>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1 flex items-center gap-1.5"><Target className="w-3.5 h-3.5"/> Crossover Alert</div>
                    <div className={`text-sm font-black mt-1 ${signals.breakout === 'BULLISH CROSSOVER' ? 'text-emerald-700' : (signals.breakout === 'BEARISH CROSSDOWN' ? 'text-red-700' : 'text-slate-400')}`}>{signals.breakout}</div>
                </Card>
            </div>
          )}
        </aside>

        {/* RIGHT PANEL: SYNCHRONIZED CHARTS & AI INSIGHTS */}
        <main className="flex-1 flex flex-col min-w-0 gap-3">
          
          <Card className="flex-1 p-4 shadow-sm border-slate-200 bg-white/90 backdrop-blur-md flex flex-col min-h-0 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[100px] font-black text-slate-100 opacity-40 pointer-events-none z-0">
              {symbol}
            </div>

            <div className="flex justify-between items-end mb-2 shrink-0 z-10 relative">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-800">Master Price Action <span className="text-slate-400 font-semibold text-xs ml-2">EMA({ema}) & SMA({sma})</span></h3>
                  
                  <div className="flex items-center gap-4 mt-1.5">
                    <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">Period High: ₹{viewStats.high}</span>
                    <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">Period Low: ₹{viewStats.low}</span>
                    <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">Avg RSI: {viewStats.avgRsi}</span>
                  </div>
                </div>
                
                {/* Data Points / View Range Controller */}
                <div className="flex items-center gap-2">
                   <Label className="text-[10px] font-bold text-slate-400 uppercase">View Range</Label>
                   <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                     {[30, 90, 150, 300].map((len) => (
                        <button
                          key={len} onClick={() => setTailLength(len)}
                          className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors ${tailLength === len ? 'bg-white shadow-sm text-sky-600' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          {len}
                        </button>
                     ))}
                   </div>
                   <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-800" title="Full Screen Coming Soon"><Maximize className="w-3.5 h-3.5" /></Button>
                </div>
            </div>
            
            <div className="flex-1 min-h-0 z-10 relative mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={tail} margin={{ top: 5, right: 0, left: -25, bottom: 0 }} syncId="proChartSync">
                  <defs>
                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f172a" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" hide />
                  <YAxis domain={["auto", "auto"]} width={80} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 600}} tickLine={false} axisLine={false} orientation="right" />
                  <Tooltip content={<CustomPriceTooltip />} cursor={{ stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '3 3' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 600, marginTop: '-5px' }}/>
                  
                  <Area type="monotone" dataKey="close" stroke="#0f172a" strokeWidth={2.5} fillOpacity={1} fill="url(#colorPrice)" name="Close" />
                  <Line type="monotone" dataKey={emaK} stroke="#0ea5e9" strokeWidth={1.5} dot={false} name={`EMA(${ema})`} />
                  <Line type="monotone" dataKey={smaK} stroke="#f59e0b" strokeWidth={1.5} dot={false} name={`SMA(${sma})`} />
                  <Line type="step" dataKey="supertrend" stroke="#10b981" strokeWidth={1.5} dot={false} name="Supertrend" strokeDasharray="4 4" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* LOWER SECTION: RSI CHART & AI INSIGHT PANEL */}
          <div className="flex gap-3 h-[180px] shrink-0">
            <Card className="flex-1 p-4 shadow-sm border-slate-200 bg-white/90 backdrop-blur-md flex flex-col relative z-0">
              <h3 className="text-xs font-extrabold text-slate-800 mb-1 shrink-0">Momentum Oscillator <span className="text-slate-400 font-semibold ml-2">RSI ({rsiP})</span></h3>
              <div className="flex-1 min-h-0 mt-1">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tail} margin={{ top: 5, right: 0, left: -25, bottom: 0 }} syncId="proChartSync">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="date" tick={{fontSize: 9, fill: '#94a3b8'}} tickLine={false} axisLine={false} minTickGap={30} />
                    <YAxis domain={[0, 100]} width={80} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 600}} tickLine={false} axisLine={false} orientation="right" ticks={[30, 50, 70]} />
                    
                    {/* Simplified Tooltip for RSI since data is merged into Master Tooltip */}
                    <Tooltip 
                       cursor={{ stroke: '#64748b', strokeWidth: 1.5, strokeDasharray: '3 3' }} 
                       content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                             return (
                               <div className="bg-slate-900 text-white p-2 rounded-lg text-xs font-mono font-bold shadow-xl border border-slate-700">
                                 RSI: {payload[0].value.toFixed(2)}
                               </div>
                             );
                          }
                          return null;
                       }}
                    />
                    
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" label={{ position: 'insideTopLeft', value: 'Overbought', fill: '#ef4444', fontSize: 9, fontWeight: 700 }} />
                    <ReferenceLine y={30} stroke="#10b981" strokeDasharray="3 3" label={{ position: 'insideBottomLeft', value: 'Oversold', fill: '#10b981', fontSize: 9, fontWeight: 700 }} />
                    
                    <Line type="monotone" dataKey={rsiK} stroke="#8b5cf6" strokeWidth={2} dot={false} name={`RSI`} activeDot={{ r: 5, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* 🔥 NEW FEATURE: AI Actionable Insight Panel 🔥 */}
            <Card className="w-[300px] p-4 shadow-sm border-slate-200 bg-slate-900 flex flex-col justify-between">
               <div>
                  <h3 className="text-xs font-black text-sky-400 uppercase tracking-widest flex items-center gap-1.5 mb-2"><Crosshair className="w-3.5 h-3.5"/> Actionable Insight</h3>
                  <p className="text-[11px] text-slate-300 leading-relaxed font-medium">
                     {generateInsight()}
                  </p>
               </div>
               <div className="mt-3 text-[9px] text-slate-500 font-bold uppercase tracking-wider border-t border-slate-800 pt-2 text-right">
                  Algorithmic Compilation
               </div>
            </Card>
          </div>

        </main>
      </div>
    </div>
  );
}